import { useMemo } from "react";
import { format, startOfDay, isSameDay } from "date-fns";
import { useQueryState, parseAsString } from "nuqs";
import {
  EventFragment,
  ActivityFragment,
  ProjectFragment,
} from "../../../../../../__generated__/graphql";
import PlanningView from "../../../../../../components/PlanningView";
import CreateActivityButton from "../EventPlanning/components/CreateActivityButton";
import CreateEventButton from "../../../../../events/components/CreateEventButton";

interface ProjectPlanningContentProps {
  project: ProjectFragment;
  onNavigateToEventDetails?: (event: EventFragment) => void;
}

export default function ProjectPlanningContent({
  project,
  onNavigateToEventDetails,
}: ProjectPlanningContentProps) {
  // Query state for navigating to a specific date
  const [dateString, setDateString] = useQueryState(
    "date",
    parseAsString.withDefault(format(new Date(), "yyyy-MM-dd"))
  );

  // Callback when an activity is created - focus the planning view on the activity's start date
  const handleActivityCreated = (startDate: Date) => {
    setDateString(format(startDate, "yyyy-MM-dd"));
  };

  // Get the closest day from today containing events or activities
  // Priority: today > closest future day > most recent past day
  const defaultDate = useMemo(() => {
    const allDates: Date[] = [];

    // Collect event dates
    project.events?.forEach((event) => {
      if (event) {
        allDates.push(new Date(event.startDate));
      }
    });

    // Collect project-level activity dates
    project.activities?.forEach((activity) => {
      if (activity) {
        allDates.push(new Date(activity.startDate));
      }
    });

    // Collect event-level activity dates
    project.events?.forEach((event) => {
      event?.activities?.forEach((activity) => {
        if (activity) {
          allDates.push(new Date(activity.startDate));
        }
      });
    });

    if (allDates.length === 0) return null;

    const today = startOfDay(new Date());

    // Check if today has any events/activities
    const hasToday = allDates.some((date) => isSameDay(date, today));
    if (hasToday) return today;

    // Separate future and past dates
    const futureDates = allDates
      .filter((date) => startOfDay(date) > today)
      .sort((a, b) => a.getTime() - b.getTime());

    const pastDates = allDates
      .filter((date) => startOfDay(date) < today)
      .sort((a, b) => b.getTime() - a.getTime()); // Most recent first

    // Return closest future day if available, otherwise most recent past day
    if (futureDates.length > 0) {
      return futureDates[0];
    }

    if (pastDates.length > 0) {
      return pastDates[0];
    }

    return null;
  }, [project.events, project.activities]);

  // Get project events
  const events = useMemo(() => {
    return (
      project.events?.filter(
        (event): event is EventFragment => event !== null
      ) ?? []
    );
  }, [project.events]);

  // Aggregate activities from project and all project events
  const activities = useMemo(() => {
    const allActivities: ActivityFragment[] = [];

    // Add project-level activities (activities linked directly to the project)
    if (project.activities) {
      project.activities.forEach((activity) => {
        if (activity) {
          allActivities.push(activity);
        }
      });
    }

    // Add event-level activities (activities linked to events within the project)
    events.forEach((event) => {
      if (event.activities) {
        event.activities.forEach((activity) => {
          if (activity) {
            allActivities.push(activity);
          }
        });
      }
    });

    return allActivities;
  }, [project.activities, events]);

  // Get project participants
  const projectParticipants = useMemo(() => {
    return (
      project.projectParticipants
        ?.filter((p) => p !== null)
        .map((p) => p.publicUserOrganization) ?? []
    );
  }, [project.projectParticipants]);

  return (
    <div className="w-full flex flex-col gap-4 border border-input rounded-md p-4">
      <div className="text-xl font-bold">Planning</div>

      <PlanningView
        events={events}
        activities={activities}
        showProjectFilter={false}
        participants={projectParticipants}
        defaultDate={defaultDate}
        onNavigateToEventDetails={onNavigateToEventDetails}
        actions={
          <div className="flex gap-2">
            <CreateActivityButton
              projectId={project.id}
              onSuccess={handleActivityCreated}
              defaultStartDate={dateString}
            />
            <CreateEventButton
              projectId={project.id}
              defaultStartDate={dateString}
              onEventCreated={onNavigateToEventDetails}
            />
          </div>
        }
      />
    </div>
  );
}
